import {
  DefaultButton,
  DialogFooter,
  IconButton,
  Label,
  mergeStyleSets,
} from "@fluentui/react";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import ErrorMsg from "components/controls/ErrorMsg";
import TextInputField from "components/controls/TextInputField";
import { EActionDialog } from "pages/fleet-form/FleetForm/constants";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";

// Formik field (and flat error key) of an option's text.
export const assistanceOptionFieldName = (index: number) => `options.${index}`;

const AssistanceOptionsFormFields = ({
  fieldProps,
  insurerOptions,
  isLoading,
}) => {
  const classes = getClassNames();
  const { t } = useTranslation();
  const { setFieldValue, values, errors, handleChange } = fieldProps;
  const isReadOnly =
    useSelector((s) => s.fleetForm.dialog.type) === EActionDialog.ReadOnly;

  const options: string[] = values.options || [];

  const addOption = () => setFieldValue("options", [...options, ""]);

  const removeOption = (index: number) =>
    setFieldValue(
      "options",
      options.filter((_, i) => i !== index),
    );

  return (
    <>
      <div className={classes.row}>
        <div className={classes.fieldItem} style={{ marginRight: "10px" }}>
          <DatePickerField
            errors={errors}
            setFieldValue={setFieldValue}
            defaultValue={values.validFromDate}
            name={"validFromDate"}
            disabled={isReadOnly}
            label={"bfm.vehicleForm.validFromDate.label"}
            placeholder={t("bfm.selectDate")}
            required
            confidentiality={null}
          />
        </div>
        <div className={classes.fieldItem} style={{ marginLeft: "10px" }}>
          <ComboBoxField
            id="insurerPartnerNumber"
            disabled={isReadOnly}
            errors={errors}
            required
            name="insurerPartnerNumber"
            label={"bfm.insurer.label"}
            setFieldValue={setFieldValue}
            options={insurerOptions || []}
            defaultValue={values?.insurerPartnerNumber}
            confidentiality={null}
          />
        </div>
      </div>

      <Label required className={classes.optionsLabel}>
        {t("bfm.assistanceOptions.label")}
      </Label>
      {options.map((option, index) => (
        <div key={index} className={classes.row}>
          <div className={classes.fieldItem}>
            <TextInputField
              value={option}
              onChange={handleChange}
              errors={errors}
              name={assistanceOptionFieldName(index)}
              disabled={isReadOnly}
              required
              label={"bfm.assistanceOptions.option.label"}
            />
          </div>
          <div className={classes.removeButton}>
            {/* at least one option */}
            {!isReadOnly && options.length > 1 && (
              <IconButton
                iconProps={{ iconName: "Cancel" }}
                title={t("greco.delete")}
                ariaLabel={t("greco.delete")}
                onClick={() => removeOption(index)}
              />
            )}
          </div>
        </div>
      ))}
      {errors.options && typeof errors.options === "string" && (
        <ErrorMsg>{errors.options}</ErrorMsg>
      )}
      {!isReadOnly && (
        <DefaultButton
          className={classes.addButton}
          text={t("bfm.assistanceOptions.addOption.label")}
          iconProps={{ iconName: "Add" }}
          onClick={addOption}
        />
      )}

      <div className={classes.placeholder}></div>
      {!isReadOnly && (
        <DialogFooter className={classes.footer}>
          <DefaultButton
            id="saveButton"
            primary
            text={t("greco.save")}
            type="submit"
            iconProps={{
              iconName: "Save",
            }}
            disabled={isLoading}
          />
        </DialogFooter>
      )}
    </>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    row: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
    },
    fieldItem: {
      width: "100%",
      marginTop: 10,
    },
    optionsLabel: {
      marginTop: 20,
    },
    removeButton: {
      width: 40,
      flexShrink: 0,
      paddingTop: 38,
    },
    addButton: {
      marginTop: 15,
    },
    placeholder: {
      height: 100,
    },
    footer: {
      marginTop: 50,
    },
  });

export default AssistanceOptionsFormFields;
